import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Lightbox from "@/components/feature/Lightbox";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { galleryList } from "@/mocks/gallery";
import { suggestKeywords } from "@/mocks/special";
import { formatChineseDate, formatDotDate } from "@/utils/format";

const tabs = [
  { key: "news", label: "新闻", icon: "ri-newspaper-line" },
  { key: "works", label: "影视", icon: "ri-film-line" },
  { key: "gallery", label: "写真", icon: "ri-image-line" },
] as const;

type TabKey = (typeof tabs)[number]["key"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<TabKey>("news");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useDocumentMeta({
    title: keyword ? `搜索「${keyword}」- 张柏芝资讯站` : "站内搜索 - 张柏芝资讯站",
    description: keyword
      ? `在张柏芝资讯站中搜索「${keyword}」，可查看相关新闻、影视作品与写真图集的结果。`
      : "输入关键词，在张柏芝资讯站的新闻、影视作品与写真图集中同时检索。",
    keywords: "站内搜索,张柏芝搜索,新闻检索,作品检索,写真检索",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const lower = keyword.trim().toLowerCase();

  const newsResults = useMemo(() => {
    if (!lower) return [];
    return newsList.filter((item) =>
      `${item.title}${item.summary}${item.category}${item.paragraphs.join("")}`.toLowerCase().includes(lower),
    );
  }, [lower]);

  const workResults = useMemo(() => {
    if (!lower) return [];
    return worksList.filter((item) =>
      `${item.title}${item.role}${item.type}${item.year}${item.synopsis}${item.director}`.toLowerCase().includes(lower),
    );
  }, [lower]);

  const galleryResults = useMemo(() => {
    if (!lower) return [];
    return galleryList.filter((item) =>
      `${item.title}${item.caption}${item.category}`.toLowerCase().includes(lower),
    );
  }, [lower]);

  const resultMap: Record<TabKey, number> = {
    news: newsResults.length,
    works: workResults.length,
    gallery: galleryResults.length,
  };

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setParams({});
      return;
    }
    setParams({ q: trimmed });
  };

  const totalResults = resultMap.news + resultMap.works + resultMap.gallery;

  return (
    <SiteLayout>
      <PageHero
        kicker="Search"
        title="站内搜索"
        description="输入关键词即可在新闻、影视作品与写真图集三大分类中同时检索。搜索基于站内示范数据，结果为前端演示效果。"
      >
        <Breadcrumb items={[{ label: "搜索结果" }]} />
      </PageHero>

      <div className="w-full px-4 py-8 md:px-8 md:py-12 lg:px-12">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit(input);
          }}
          className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 p-1.5 focus-within:border-primary-400"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-base" aria-hidden="true"></i>
          </span>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索新闻标题、作品名称、角色或写真主题"
            aria-label="搜索关键词"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          {input && (
            <button
              type="button"
              onClick={() => {
                setInput("");
                setParams({});
              }}
              aria-label="清空关键词"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-close-line" aria-hidden="true"></i>
            </button>
          )}
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600 md:text-sm"
          >
            搜索
          </button>
        </form>

        {!lower ? (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-900">输入关键词开始搜索</p>
            <p className="mt-2 text-xs text-foreground-500">可以试试下面这些关键词：</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestKeywords.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setInput(item);
                    submit(item);
                  }}
                  className="cursor-pointer rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-col gap-4 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {tabs.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setTab(item.key)}
                    aria-pressed={tab === item.key}
                    className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                      tab === item.key
                        ? "bg-background-50 text-primary-700"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    <i className={item.icon} aria-hidden="true"></i>
                    {item.label}
                    <span className="text-[10px] text-foreground-400">{resultMap[item.key]}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-foreground-500">
                关键词「<span className="text-foreground-900">{keyword}</span>」共找到 {totalResults} 条结果
              </p>
            </div>

            <div className="mt-6">
              {tab === "news" && (
                <div className="flex flex-col gap-3">
                  {newsResults.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4"
                    >
                      <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-24 md:w-36">
                        <img
                          src={item.cover}
                          alt={`${item.title} - 张柏芝新闻配图`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                          <span className="rounded-sm bg-primary-100 px-2 py-0.5 text-primary-800">{item.category}</span>
                          <span>{formatChineseDate(item.publishedAt)}</span>
                        </div>
                        <h2 className="mt-1.5 line-clamp-2 font-heading text-sm font-semibold leading-6 text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                          {item.title}
                        </h2>
                        <p className="mt-1 line-clamp-2 text-xs leading-6 text-foreground-600">{item.summary}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {tab === "works" && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {workResults.map((item) => (
                    <Link
                      key={item.id}
                      to={`/works/${item.id}`}
                      className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <div className="h-28 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 海报 - 张柏芝影视作品`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                          <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-accent-900">{item.type}</span>
                          <span>{item.year}</span>
                        </div>
                        <h2 className="mt-1.5 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </h2>
                        <p className="mt-1 text-[11px] text-foreground-600">饰演 {item.role}</p>
                        <p className="mt-1.5 line-clamp-2 text-xs leading-6 text-foreground-600">{item.synopsis}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {tab === "gallery" && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                  {galleryResults.map((item) => {
                    const globalIndex = galleryList.findIndex((entry) => entry.id === item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setLightboxIndex(globalIndex)}
                        className="group relative aspect-[3/4] cursor-pointer overflow-hidden rounded-lg bg-background-200"
                      >
                        <img
                          src={item.image}
                          alt={`${item.title} - 张柏芝${item.category}图片`}
                          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                        />
                        <span className="absolute left-2 top-2 rounded-full bg-background-50/90 px-2 py-0.5 text-[10px] text-foreground-800">
                          {item.category}
                        </span>
                        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/85 to-transparent px-3 pb-2.5 pt-8 text-left">
                          <span className="block truncate text-[11px] text-background-50">{item.title}</span>
                          <span className="mt-0.5 block text-[10px] text-background-300">{formatDotDate(item.date)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {resultMap[tab] === 0 && (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <p className="mt-4 text-sm font-medium text-foreground-900">
                    在「{tabs.find((item) => item.key === tab)?.label}」分类中没有找到相关结果
                  </p>
                  <p className="mt-2 max-w-md text-xs leading-6 text-foreground-500">
                    可以尝试更简短的关键词，或切换到其它分类查看。以下关键词通常能找到内容：
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {suggestKeywords.slice(0, 6).map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => {
                          setInput(item);
                          submit(item);
                        }}
                        className="cursor-pointer rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          items={galleryList}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((current) => ((current ?? 0) - 1 + galleryList.length) % galleryList.length)}
          onNext={() => setLightboxIndex((current) => ((current ?? 0) + 1) % galleryList.length)}
          onSelect={(target) => setLightboxIndex(target)}
        />
      )}
    </SiteLayout>
  );
}